import { faPaperclip } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Checkbox,
  FlexBox,
  JoonUIColor,
  QuestInstance,
  QuestStatus,
  SPACING,
  TherapistIcon,
  Typography,
  UserRole,
} from "@joonapp/web-shared"
import { useState } from "react"

import useQuestBoardMultiSelectStore from "./useQuestBoardMultiSelectStore"
import useQuestBoardStore from "./useQuestBoardStore"
import { useReviewQuestModalStore } from "./useReviewQuestModalStore"
import { useQuestActionsModalStore } from "./viewQuestModal/QuestActionsModal"
import Card from "../../components/card/Card"
import { QuestSectionType } from "../../types/quests"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import {
  getDayRepetitionString,
  getDaysFromRRuleString,
  getQuestCardBorderColor,
  getQuestCardTabName,
  isCoachQuest,
} from "../../util/util"
import CareTeamAvatar from "../care/CareTeamAvatar"
import useCareTeam from "../care/useCareTeam"

type Props = {
  questInstance: QuestInstance
  type: QuestSectionType
  index: number
}

const QuestCard = ({ questInstance, type, index }: Props) => {
  const { onOpen: onOpenQuestActionsModal } = useQuestActionsModalStore()
  const { onOpen: openReviewQuestModal, setQuest: setQuestToReview } =
    useReviewQuestModalStore()
  const { selectedUserId } = useQuestBoardStore()

  const { selectedQuestIds, toggleSelectedQuestId } =
    useQuestBoardMultiSelectStore()

  const [isHovered, setIsHovered] = useState(false)
  const [isHoveredOnCheckbox, setIsHoveredOnCheckbox] = useState(false)

  const tabName = getQuestCardTabName(type, index)
  const isMandatory = questInstance?.series?.mandatory
  const borderColor = getQuestCardBorderColor(type, isMandatory)
  const isSelected = selectedQuestIds.includes(questInstance.id)

  const byday = getDaysFromRRuleString(questInstance?.series?.recurrence)
  const subtitle = (
    <div className="flex flex-row items-center gap-1">
      {getDayRepetitionString(byday, questInstance?.series?.reminder_time)}
      {questInstance?.series?.resource && (
        <FontAwesomeIcon
          icon={faPaperclip}
          fontSize={12}
          color={JoonUIColor.icon.accent}
        />
      )}
    </div>
  ) as any

  const onClickCard = () => {
    if (questInstance.status === QuestStatus.COMPLETED) {
      setQuestToReview(questInstance)
      openReviewQuestModal()
      return
    } else {
      trackAnalyticEvent(ANALYTIC_EVENTS.OPEN_QUEST_STATUS_MODAL)
      onOpenQuestActionsModal(questInstance)
    }
  }

  return (
    <div
      style={{
        width: "100%",
        transform: isHovered ? "scale(1.015)" : "scale(1)",
        transition: "transform 0.2s",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {tabName && (
        <div
          style={{
            padding: `${SPACING.space0} ${SPACING.space2}`,
            background: borderColor,
            borderRadius: `${SPACING.space2} ${SPACING.space2} 0 0`,
            marginLeft: SPACING.space3,
            width: "fit-content",
            display: "flex",
            alignItems: "center",
            height: SPACING.space5,
          }}
        >
          <Typography
            variant="bodyXSmall"
            color={JoonUIColor.text.inverted}
            style={{ lineHeight: SPACING.space5 }}
          >
            {tabName}
          </Typography>
        </div>
      )}
      <FlexBox
        wrap={false}
        style={{
          background: "rgba(255, 255, 255, 0.6)",
          border:
            borderColor && !tabName
              ? `1px solid ${JoonUIColor.border.default}`
              : undefined,
          borderRadius: SPACING.space2,
          width: "100%",
          overflow: "hidden",
          maxWidth: "100%",
          height: "fit-content",
        }}
        justify="unset"
        align="center"
      >
        <Card
          title={<QuestCardTitle questInstance={questInstance} />}
          subtitle={subtitle}
          userIds={!selectedUserId ? [questInstance.user_id] : undefined}
          coinAmount={questInstance?.series?.redeemable_reward}
          borderColor={borderColor}
          onClick={onClickCard}
          style={{
            maxWidth:
              type === QuestSectionType.ROUTINE ? "calc(100% - 54px)" : "100%",
          }}
        />
        {type === QuestSectionType.ROUTINE && (
          <div
            onMouseEnter={() => setIsHoveredOnCheckbox(true)}
            onMouseLeave={() => setIsHoveredOnCheckbox(false)}
            style={{
              transform: isHoveredOnCheckbox ? "scale(1.1)" : "scale(1)",
              transition: "transform 0.2s",
            }}
          >
            <Checkbox
              name={questInstance.id.toString()}
              selected={isSelected}
              onChange={() => toggleSelectedQuestId(questInstance.id)}
              hideBorder
              style={{
                padding: SPACING.space4,
                background: "transparent",
              }}
            />
          </div>
        )}
      </FlexBox>
    </div>
  )
}

export default QuestCard

export const QuestCardTitle = ({
  questInstance,
}: {
  questInstance: QuestInstance
}) => {
  const { careTeam } = useCareTeam()
  const assignedCoach = careTeam?.find(
    (profile) =>
      profile.user.id === questInstance?.series?.assigner_profile?.user_id
  )
  const showTherapistIcon =
    questInstance?.series?.current_frequency_target ||
    questInstance?.series?.assigner_profile?.role === UserRole.THERAPIST
  const showCoachAvatar =
    isCoachQuest(questInstance) && assignedCoach?.profile_image_url

  return (
    <FlexBox align="center" gap={SPACING.space1} wrap={false}>
      {showTherapistIcon && (
        <TherapistIcon size={16} color={JoonUIColor.background.accent} />
      )}
      {showCoachAvatar && <CareTeamAvatar profile={assignedCoach} size="xs" />}
      <Typography
        variant="bodySmallBold"
        style={{
          width: "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {questInstance?.series?.title.slice(0, 72)}
      </Typography>
    </FlexBox>
  )
}
